import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";

import {
  ErrorAlert,
  formatDate,
  Loading,
  PageHeader,
  StatusBadge,
} from "../../layout/secure/common";
import { SortButton } from "../../layout/secure/result-table";
import ExportControls from "../../layout/secure/export-controls";
import {
  currentUsage,
  getAggregateReport,
  messageFromError,
} from "../../utils/api";
import { AnswerValue, reportQuestionLabel } from "./response-report";

const EMPTY_FILTERS = {
  search: "",
  status: "",
  source: "",
  submittedFrom: "",
  submittedTo: "",
};

function QuestionAggregate({ item }) {
  return (
    <section className="fera-report-section fera-question-aggregate">
      <div className="fera-panel-heading">
        <div>
          <p className="fera-eyebrow">{item.sectionKey}</p>
          <h2>{reportQuestionLabel(item.label, item.questionKey)}</h2>
        </div>
        <span>{item.answered} answered · {item.omitted} omitted</span>
      </div>
      {item.kind === "options" ? (
        <div className="fera-option-aggregates">
          {item.options.map(option => (
            <div key={option.label}>
              <div><span>{option.label}</span><strong>{option.count} ({option.percentage.toFixed(1)}%)</strong></div>
              <span className="fera-option-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(option.percentage, 100)}%` }} />
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {item.kind === "numeric" ? (
        <dl className="fera-metric-grid">
          <div><dt>Count</dt><dd>{item.numeric.count}</dd></div>
          <div><dt>Average</dt><dd>{item.numeric.average ?? "\u2014"}</dd></div>
          <div><dt>Minimum</dt><dd>{item.numeric.minimum ?? "\u2014"}</dd></div>
          <div><dt>Maximum</dt><dd>{item.numeric.maximum ?? "\u2014"}</dd></div>
          <div><dt>Sum</dt><dd>{item.numeric.sum}</dd></div>
        </dl>
      ) : null}
      {item.kind === "date" ? (
        <dl className="fera-metric-grid">
          <div><dt>Date values</dt><dd>{item.dates.count}</dd></div>
          <div><dt>Earliest</dt><dd>{item.dates.earliest ? formatDate(item.dates.earliest) : "\u2014"}</dd></div>
          <div><dt>Latest</dt><dd>{item.dates.latest ? formatDate(item.dates.latest) : "\u2014"}</dd></div>
        </dl>
      ) : null}
      {item.kind === "collection" ? (
        <dl className="fera-metric-grid">
          <div><dt>Responses</dt><dd>{item.collection.responses}</dd></div>
          <div><dt>Total items</dt><dd>{item.collection.items}</dd></div>
          <div><dt>Average items</dt><dd>{item.collection.averageItems.toFixed(2)}</dd></div>
        </dl>
      ) : null}
      {item.kind === "text" ? (
        item.text.length ? (
          <div className="table-responsive">
            <Table className="fera-table" size="sm">
              <thead><tr><th>Respondent</th><th>Answer</th></tr></thead>
              <tbody>
                {item.text.map((answer, index) => (
                  <tr key={`${answer.responseId}-${index}`}>
                    <td>{answer.respondent}</td>
                    <td><AnswerValue value={answer.value} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : <p className="text-muted mb-0">No text answers in this filtered report.</p>
      ) : null}
    </section>
  );
}

function AggregateReport() {
  const { surveyId } = useParams();
  const [report, setReport] = useState(null);
  const [branding, setBranding] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [questionCatalog, setQuestionCatalog] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [sort, setSort] = useState("submittedAt");
  const [direction, setDirection] = useState("desc");

  const load = useCallback(async (
    filterValues = applied,
    questionValues = selectedQuestions
  ) => {
    setError("");
    try {
      const parameters = Object.fromEntries(
        Object.entries(filterValues).filter(([, value]) => value)
      );
      if (
        questionValues.length
        && questionCatalog.length
        && questionValues.length !== questionCatalog.length
      ) {
        parameters.questions = questionValues.join(",");
      }
      const [result, usageData] = await Promise.all([
        getAggregateReport(surveyId, parameters),
        usage ? Promise.resolve(null) : currentUsage(),
      ]);
      setReport(result);
      if (!questionCatalog.length) {
        const catalog = result.questions.map(item => ({
          key: `${item.sectionKey}.${item.questionKey}`,
          label: reportQuestionLabel(item.label, item.questionKey),
        }));
        setQuestionCatalog(catalog);
        setSelectedQuestions(catalog.map(item => item.key));
      }
      if (usageData) {
        setUsage(usageData);
        if (usageData.branding) setBranding(usageData.branding);
      }
    } catch (requestError) {
      setError(messageFromError(requestError, "The aggregate report could not be loaded."));
    }
  }, [applied, questionCatalog.length, selectedQuestions, surveyId, usage]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getAggregateReport(surveyId),
      currentUsage().catch(() => null),
    ])
      .then(([result, usage]) => {
        if (!active) return;
        setReport(result);
        setBranding(usage?.branding || null);
        setUsage(usage);
        const catalog = result.questions.map(item => ({
          key: `${item.sectionKey}.${item.questionKey}`,
          label: reportQuestionLabel(item.label, item.questionKey),
        }));
        setQuestionCatalog(catalog);
        setSelectedQuestions(catalog.map(item => item.key));
      })
      .catch(requestError => {
        if (active) {
          setError(messageFromError(requestError, "The aggregate report could not be loaded."));
        }
      });
    return () => { active = false; };
  }, [surveyId]);

  const sortedResults = useMemo(() => {
    const items = [...(report?.results || [])];
    const value = item => {
      if (sort === "respondent") return item.respondent.displayName.toLowerCase();
      return item[sort] || "";
    };
    items.sort((left, right) => {
      const a = value(left);
      const b = value(right);
      const comparison = a < b ? -1 : a > b ? 1 : 0;
      return direction === "asc" ? comparison : -comparison;
    });
    return items;
  }, [direction, report?.results, sort]);

  const changeSort = key => {
    if (sort === key) setDirection(value => value === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setDirection("asc");
    }
  };

  const change = event => {
    const { name, value } = event.target;
    setFilters(current => ({
      ...current,
      [name]: value,
      ...(name === "status" && value === "draft"
        ? { submittedFrom: "", submittedTo: "" }
        : {}),
    }));
  };

  if (!report && !error) return <Loading label="Building aggregate report" />;
  if (!report) return <ErrorAlert error={error} onRetry={load} />;

  return (
    <>
      <div className="fera-print-hidden">
        <PageHeader
          title="Aggregate report"
          description="Question-level summaries and a sortable response list."
          action={(
            <div className="fera-page-buttons">
              <Button as={Link} variant="outline-secondary" to={`/dashboard/surveys/${surveyId}/results`}>Results</Button>
              <Button variant="outline-primary" onClick={() => window.print()}>Print</Button>
            </div>
          )}
        />
        <ErrorAlert error={error} onRetry={load} />
        <section className="fera-panel mb-4">
          <Form onSubmit={event => {
            event.preventDefault();
            setApplied(filters);
            load(filters, selectedQuestions);
          }}>
            <div className="fera-result-filters">
              <Form.Group controlId="aggregate-search">
                <Form.Label>Respondent</Form.Label>
                <Form.Control name="search" value={filters.search} onChange={change} placeholder="Name or verified email" />
              </Form.Group>
              <Form.Group controlId="aggregate-status">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={filters.status} onChange={change}>
                  <option value="">All statuses</option>
                  <option value="submitted">Submitted</option>
                  <option value="draft">Draft</option>
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="aggregate-source">
                <Form.Label>Source</Form.Label>
                <Form.Select name="source" value={filters.source} onChange={change}>
                  <option value="">All sources</option>
                  <option value="generic-link">Generic link</option>
                  <option value="invitation">Invitation</option>
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="aggregate-from">
                <Form.Label>Submitted from</Form.Label>
                <Form.Control type="date" name="submittedFrom" value={filters.submittedFrom} onChange={change} disabled={filters.status === "draft"} />
              </Form.Group>
              <Form.Group controlId="aggregate-to">
                <Form.Label>Submitted to</Form.Label>
                <Form.Control type="date" name="submittedTo" value={filters.submittedTo} onChange={change} disabled={filters.status === "draft"} />
              </Form.Group>
              <div className="fera-filter-actions">
                <Button type="submit">Apply filters</Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => {
                    setFilters(EMPTY_FILTERS);
                    setApplied(EMPTY_FILTERS);
                    const allQuestions = questionCatalog.map(item => item.key);
                    setSelectedQuestions(allQuestions);
                    load(EMPTY_FILTERS, allQuestions);
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>
            {questionCatalog.length ? (
              <fieldset className="fera-question-filter mt-3">
                <legend>Questions in report</legend>
                {questionCatalog.map(item => (
                  <Form.Check
                    key={item.key}
                    type="checkbox"
                    id={`aggregate-${item.key}`}
                    label={item.label}
                    checked={selectedQuestions.includes(item.key)}
                    onChange={event => {
                      setSelectedQuestions(current => event.target.checked
                        ? [...current, item.key]
                        : current.length > 1
                          ? current.filter(key => key !== item.key)
                          : current);
                    }}
                  />
                ))}
              </fieldset>
            ) : null}
          </Form>
        </section>
      </div>

      <article className="fera-report" aria-labelledby="aggregate-report-title">
        <header className="fera-report-header">
          <div>
            {branding?.smallLogo ? <img src={branding.smallLogo} alt="" /> : null}
            <p className="fera-eyebrow">{branding?.name || "FeraSurvey"}</p>
            <h1 id="aggregate-report-title">{report.survey.name}</h1>
            <p>Aggregate HTML report</p>
          </div>
          <div className="fera-report-status"><StatusBadge status={report.survey.status} /></div>
        </header>

        <section className="fera-report-section">
          <h2>Survey summary</h2>
          <dl className="fera-metric-grid">
            <div><dt>Total results</dt><dd>{report.summary.total}</dd></div>
            <div><dt>Submitted</dt><dd>{report.summary.submitted}</dd></div>
            <div><dt>Drafts</dt><dd>{report.summary.drafts}</dd></div>
            <div><dt>Invited</dt><dd>{report.summary.invited}</dd></div>
            <div><dt>Started</dt><dd>{report.summary.started}</dd></div>
            <div>
              <dt>Response range</dt>
              <dd>
                {report.summary.responseDateRange.earliest
                  ? `${formatDate(report.summary.responseDateRange.earliest)} – ${formatDate(report.summary.responseDateRange.latest)}`
                  : "No submitted dates"}
              </dd>
            </div>
          </dl>
        </section>

        {report.questions.length ? report.questions.map(item => (
          <QuestionAggregate key={`${item.sectionKey}.${item.questionKey}`} item={item} />
        )) : (
          <Alert variant="info">No submitted answers match the selected report questions and filters.</Alert>
        )}

        <section className="fera-report-section">
          <h2>Responses</h2>
          <div className="table-responsive">
            <Table className="fera-table" hover>
              <thead>
                <tr>
                  <th><SortButton active={sort === "respondent"} direction={direction} onClick={() => changeSort("respondent")}>Respondent</SortButton></th>
                  <th>Source</th>
                  <th>Status</th>
                  <th><SortButton active={sort === "startedAt"} direction={direction} onClick={() => changeSort("startedAt")}>Started</SortButton></th>
                  <th><SortButton active={sort === "submittedAt"} direction={direction} onClick={() => changeSort("submittedAt")}>Submitted</SortButton></th>
                  <th>Version</th>
                  <th className="fera-print-hidden"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.map(item => (
                  <tr key={item.id}>
                    <td>{item.respondent.displayName}</td>
                    <td>{item.source.label}</td>
                    <td>{item.status}</td>
                    <td>{formatDate(item.startedAt)}</td>
                    <td>{item.submittedAt ? formatDate(item.submittedAt) : "\u2014"}</td>
                    <td>v{item.version}</td>
                    <td className="text-end fera-print-hidden">
                      <Button
                        as={Link}
                        size="sm"
                        variant="outline-primary"
                        to={`/dashboard/surveys/${surveyId}/results/${item.id}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </section>
      </article>
      <ExportControls surveyId={surveyId} filters={applied} usage={usage} />
    </>
  );
}

export { QuestionAggregate };
export default AggregateReport;
